import { Plus, Trash2 } from 'lucide-react';
import {
  CustomerAdditionalAddress,
  emptyCustomerAdditionalAddress,
} from '../../lib/customers';
import AddressAutocompleteModal from '../AddressAutocompleteModal';
import AddressAutocomplete from '../AddressAutocomplete';

type Props = {
  entries: CustomerAdditionalAddress[];
  onChange: (next: CustomerAdditionalAddress[]) => void;
  /** Use modal-safe autocomplete (Create/Edit customer slide-overs). */
  modalAutocomplete: boolean;
};

export default function CustomerAdditionalAddressesFields({
  entries,
  onChange,
  modalAutocomplete,
}: Props) {
  const Addr = modalAutocomplete ? AddressAutocompleteModal : AddressAutocomplete;

  const updateRow = (index: number, patch: Partial<CustomerAdditionalAddress>) => {
    onChange(entries.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index: number) => {
    onChange(entries.filter((_, i) => i !== index));
  };

  const addRow = () => {
    onChange([...entries, emptyCustomerAdditionalAddress()]);
  };

  return (
    <div className="space-y-4">
      {entries.map((row, i) => (
        <div key={i} className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 space-y-3 relative z-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Additional address {i + 1}
            </span>
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-red-500 hover:bg-red-50 active:bg-red-100 transition-colors"
              aria-label={`Remove additional address ${i + 1}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Label (optional)</label>
            <input
              type="text"
              value={row.label ?? ''}
              onChange={(e) => updateRow(i, { label: e.target.value || null })}
              placeholder="e.g. Billing, Shop, Cottage"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Address
              {modalAutocomplete && (
                <span className="text-xs text-gray-500 ml-1 font-normal">Google autocomplete</span>
              )}
            </label>
            <Addr
              value={row.address}
              onChange={(address) => updateRow(i, { address })}
              onSelect={(place) =>
                updateRow(i, {
                  address: place.formatted,
                  lat: place.lat ?? null,
                  lng: place.lng ?? null,
                })
              }
              placeholder="Enter address (optional)"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-base"
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addRow}
        className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 border border-blue-200/80 rounded-xl transition-colors"
      >
        <Plus className="h-4 w-4 shrink-0" />
        Add another address
      </button>
    </div>
  );
}
